import {IconButton} from '@wandb/components';
import {RunState} from '@wandb/weave/components/RunState';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {Popup} from 'semantic-ui-react';

import DangerModal from '../components/DangerModal';
import {
  STOP_RUN,
  type StopRunMutationData,
  type StopRunMutationVariables,
} from '../graphql/runs';
import {useMutation} from '../state/graphql/apolloWithAbort';
import {useStatsigGateHideStopRunsUI} from '../util/rampFeatureFlags/rampFeatureFlags';
import {getRunStateForRun, isStopRunAvailable} from '../util/runs';

interface RunStateIndicatorProps {
  run: {
    id: string;
    config: any;
    state: string;
    stopped: boolean;
  };
  readOnly?: boolean;
  isSlurmRun?: boolean;
}

export default function RunStateIndicator(props: RunStateIndicatorProps) {
  const [stopPressed, setStopPressed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [stopRun] = useMutation<StopRunMutationData, StopRunMutationVariables>(
    STOP_RUN
  );
  const isHideStopRunsEnabled = useStatsigGateHideStopRunsUI();

  const stopRunAvailable = useMemo(() => {
    if (isHideStopRunsEnabled || props.isSlurmRun) {
      return false;
    }
    return isStopRunAvailable(props.run.config);
  }, [props.run.config, isHideStopRunsEnabled, props.isSlurmRun]);

  // NOTE: it's possible that props.run.stopped is true but that we failed to
  // actually stop the run -- in that case let the user try to stop it again.
  const canBeStopped = !stopPressed && props.run.state === 'running';

  return (
    <TailwindContents>
      <div className="flex gap-4">
        <RunState value={getRunStateForRun(props.run)} />
        {stopRunAvailable && canBeStopped ? (
          <DangerModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            trigger={
              <Popup
                position="right center"
                size="mini"
                inverted
                trigger={
                  <IconButton
                    aria-label="Stop run"
                    size="sm"
                    variant="secondary"
                    onPress={() => setModalOpen(true)}
                    icon="stop"
                  />
                }>
                {props.run.stopped
                  ? `Retry stop run (this can take several minutes)`
                  : `Stop run`}
              </Popup>
            }
            message="Are you sure you want to STOP this run? This action cannot be undone!"
            yesText="Stop this run"
            yes={() => {
              stopRun({variables: {id: props.run.id}});
              setStopPressed(true);
            }}
          />
        ) : null}
      </div>
    </TailwindContents>
  );
}
